import React from 'react';
import PropTypes from 'fe-prop-types';
import styled, { css } from 'styled-components';
import theme from 'fe-website-lib-theme';
import { Hamburger } from './Hamburger';

const PROP_TYPES = {
  icon: PropTypes.func.isRequired,
  children: PropTypes.node,
  isOpen: PropTypes.bool.isRequired,
  offsetY: PropTypes.string.isRequired,
  drawerWidth: PropTypes.string.isRequired,
  maxWidth: PropTypes.string,
  overlayOnOpen: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  zIndex: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
};

const DEFAULT_PROPS = {
  offsetY: '0px',
  drawerWidth: '100vw',
  isOpen: false,
  icon: Hamburger,
  overlayOnOpen: false,
  zIndex: 2000,
  id: 'drawer',
};

const DrawerWrapper = styled.div`
  top: ${(props) => props.offsetY};
  width: ${(props) => props.drawerWidth};
  right: -${(props) => props.drawerWidth};
  bottom: 0;
  margin: 0;
  padding: 0;
  position: fixed;
  z-index: ${(props) => props.zIndex};
  background-color: ${theme.colorPageBackground};
  overflow-y: auto;
  overflow-x: hidden;
  transition: transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  ${(props) =>
    props.isOpen
      ? css`
          transform: translateX(-${(props) => props.drawerWidth});
        `
      : ''};

  ${(props) =>
    Boolean(props.maxWidth) &&
    `@media (min-width: ${props.maxWidth}) {
      max-width: ${props.maxWidth}
      right: -${props.maxWidth};
      ${props.isOpen ? `transform: translateX(-${props.maxWidth});` : ''}
    }
  `}
`;

const BodyOverlay = styled.div`
  visibility: hidden;
  position: fixed;
  top: ${(props) => props.offsetY};
  bottom: 0;
  right: 0;
  left: 0;
  opacity: 0;
  transition: opacity 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    visibility 0s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.15s;
  background-color: ${theme.colorOverlayBackground};
  ${(props) =>
    props.isOpen
      ? 'visibility: visible; opacity: 0.5; transition-delay: 0s;'
      : ''}
`;

const StatelessDrawer = ({
  icon,
  children,
  drawerWidth,
  offsetY,
  maxWidth,
  overlayOnOpen,
  id,
  isOpen,
  onClick,
  zIndex,
}) => (
  <React.Fragment>
    {/* Pass the open state, click handler and drawer ID to the icon to use if desired */}
    {icon({ isOpen: isOpen, onClick: onClick, controls: id })}
    <DrawerWrapper
      id={id}
      isOpen={isOpen}
      offsetY={offsetY}
      drawerWidth={drawerWidth}
      aria-hidden={!isOpen}
      maxWidth={maxWidth}
      zIndex={zIndex}
      data-it="drawer"
    >
      {children}
    </DrawerWrapper>
    {overlayOnOpen ? (
      <BodyOverlay
        offsetY={offsetY}
        isOpen={isOpen}
        onClick={onClick}
        id={`${id}Overlay`}
        data-it="overlay"
        aria-hidden="true"
      />
    ) : null}
  </React.Fragment>
);

StatelessDrawer.propTypes = PROP_TYPES;
StatelessDrawer.defaultProps = DEFAULT_PROPS;

export { StatelessDrawer };
